/**
 * Staff user management — the admin-only screen that creates and manages the accounts the whole
 * dashboard signs in with. The counterpart to `apps/api/src/staff/admin-routes.ts`.
 *
 * Structure mirrors the other admin screens: a `useManagedStaffList` hook owns the list and the four
 * mutations, a create form sits below the table, and the two destructive-ish actions (reset password,
 * delete) are behind confirm dialogs the way `ContentListPage` gates its own delete. The role is a
 * plain `<Select>`, changed inline and committed on change — a role is one of three values, not a
 * free-text field worth a form.
 *
 * The server owns every rule that matters — admin-only, last-admin protection, no self-demotion,
 * unique email — and answers a refused change with a 409 whose message is in Spanish and safe to
 * show. So this screen surfaces failures rather than trying to pre-empt them: the one thing it does
 * pre-empt is hiding the role select and delete button for the admin's own row, because "you cannot
 * do this to yourself" is worth saying before the click, not after the round-trip.
 */
import {
  MIN_PASSWORD_LENGTH,
  staffRoles,
  type StaffMember,
  type StaffRole,
} from '@apuestapr/contracts';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  Field,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  icons,
  toast,
} from '@apuestapr/ui';
import { useState, type FormEvent, type ReactElement } from 'react';

import { fieldErrorsFor } from '../api/request-state.ts';
import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { staffRoleLabels } from '../shell/roleLabels.ts';
import { useManagedStaffList, type ManagedStaffList } from './hooks.ts';

const roleOptions = (
  <>
    {staffRoles.map((role) => (
      <option key={role} value={role}>
        {staffRoleLabels[role]}
      </option>
    ))}
  </>
);

/** A failure, said out loud. 403 is the role; a 400/409 carries field messages worth showing. */
const StaffFailure = ({ failure }: { readonly failure: ApiFailure }): ReactElement => {
  if (failure.kind === 'http' && failure.status === 403) {
    return <p role="alert">No tienes permiso para administrar cuentas de personal.</p>;
  }
  const fields = fieldErrorsFor(failure);
  if (fields.length > 0) {
    return (
      <div role="alert">
        {fields.map((field) => (
          <p key={`${field.path}:${field.message}`} className="m-0">
            {field.message}
          </p>
        ))}
      </div>
    );
  }
  if (failure.kind === 'malformed') {
    return <p role="alert">La API respondió con datos que no coinciden con el contrato.</p>;
  }
  return <p role="alert">No se pudo completar la acción. Inténtalo de nuevo.</p>;
};

interface StaffRowProps {
  readonly member: StaffMember;
  readonly isSelf: boolean;
  readonly busy: boolean;
  readonly managed: ManagedStaffList;
}

const StaffRow = ({ member, isSelf, busy, managed }: StaffRowProps): ReactElement => (
  <TableRow>
    <TableCell>{member.nombre}</TableCell>
    <TableCell>{member.email}</TableCell>
    <TableCell>
      {isSelf ? (
        // Your own role is not yours to change from here — the server refuses it with a 409, and
        // saying so before the click is kinder than the error toast that would otherwise follow.
        <span className="text-sm text-muted-foreground">{staffRoleLabels[member.rol]} (tú)</span>
      ) : (
        // A plain labelled Select rather than a `Field`: the column header is the visible label, so
        // the control carries an `aria-label` for a screen reader and does not repeat it on screen.
        <Select
          aria-label={`Rol de ${member.nombre}`}
          disabled={busy}
          value={member.rol}
          onChange={(event) => {
            const rol = event.currentTarget.value as StaffRole;
            if (rol === member.rol) return;
            void managed.updateRole(member.id, { rol }).then((result) => {
              if (result.ok) toast({ title: 'Rol actualizado' });
            });
          }}
        >
          {roleOptions}
        </Select>
      )}
    </TableCell>
    <TableCell>
      <div className="flex justify-end gap-2">
        <ResetPasswordDialog member={member} busy={busy} managed={managed} />
        {isSelf ? null : <DeleteStaffDialog member={member} busy={busy} managed={managed} />}
      </div>
    </TableCell>
  </TableRow>
);

const ResetPasswordDialog = ({
  member,
  busy,
  managed,
}: {
  readonly member: StaffMember;
  readonly busy: boolean;
  readonly managed: ManagedStaffList;
}): ReactElement => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const short = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPassword('');
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={busy}>
          Restablecer contraseña <span className="sr-only">de {member.email}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Restablecer la contraseña de {member.nombre}</DialogTitle>
          <DialogDescription>
            La cuenta cerrará todas sus sesiones activas y tendrá que iniciar sesión con la nueva
            contraseña.
          </DialogDescription>
        </DialogHeader>
        <Field
          label="Nueva contraseña"
          controlId={`staff-${String(member.id)}-new-password`}
          required
          description={`Al menos ${String(MIN_PASSWORD_LENGTH)} caracteres.`}
        >
          {(control) => (
            <Input
              {...control}
              type="password"
              autoComplete="new-password"
              disabled={busy}
              aria-invalid={short}
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
          )}
        </Field>
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={busy} variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            disabled={busy || password.length < MIN_PASSWORD_LENGTH}
            loading={busy}
            onClick={() => {
              void managed.resetPassword(member.id, { password }).then((result) => {
                if (result.ok) {
                  toast({ title: 'Contraseña restablecida' });
                  setPassword('');
                  setOpen(false);
                }
              });
            }}
          >
            Restablecer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeleteStaffDialog = ({
  member,
  busy,
  managed,
}: {
  readonly member: StaffMember;
  readonly busy: boolean;
  readonly managed: ManagedStaffList;
}): ReactElement => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive" disabled={busy}>
          Eliminar <span className="sr-only">{member.email}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eliminar la cuenta de {member.nombre}</DialogTitle>
          <DialogDescription>
            “{member.email}” se eliminará permanentemente y perderá el acceso de inmediato. Esta
            acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={busy} variant="outline">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={busy}
            loading={busy}
            onClick={() => {
              void managed.remove(member.id).then((result) => {
                if (result.ok) {
                  toast({ title: 'Cuenta eliminada', variant: 'destructive' });
                  setOpen(false);
                }
              });
            }}
          >
            Eliminar cuenta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface Draft {
  readonly nombre: string;
  readonly email: string;
  readonly rol: StaffRole;
  readonly password: string;
}

const emptyDraft: Draft = { nombre: '', email: '', rol: 'agente', password: '' };

const CreateStaffForm = ({
  busy,
  onCreate,
}: {
  readonly busy: boolean;
  readonly onCreate: (draft: Draft) => Promise<boolean>;
}): ReactElement => {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const complete =
    draft.nombre.trim() !== '' &&
    draft.email.trim() !== '' &&
    draft.password.length >= MIN_PASSWORD_LENGTH;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!complete) return;
    void onCreate(draft).then((created) => {
      if (created) setDraft(emptyDraft);
    });
  };

  const patch = (change: Partial<Draft>): void =>
    setDraft((current) => ({ ...current, ...change }));

  return (
    <form
      className="grid gap-3 rounded-md border border-dashed border-border p-4 sm:grid-cols-2"
      noValidate
      onSubmit={submit}
    >
      <Field label="Nombre" controlId="new-staff-nombre" required>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.nombre}
            onChange={(event) => patch({ nombre: event.currentTarget.value })}
          />
        )}
      </Field>
      <Field label="Correo" controlId="new-staff-email" required>
        {(control) => (
          <Input
            {...control}
            type="email"
            autoComplete="off"
            disabled={busy}
            value={draft.email}
            onChange={(event) => patch({ email: event.currentTarget.value })}
          />
        )}
      </Field>
      <Field label="Rol" controlId="new-staff-rol" required>
        {(control) => (
          <Select
            {...control}
            disabled={busy}
            value={draft.rol}
            onChange={(event) => patch({ rol: event.currentTarget.value as StaffRole })}
          >
            {roleOptions}
          </Select>
        )}
      </Field>
      <Field
        label="Contraseña inicial"
        controlId="new-staff-password"
        required
        description={`Al menos ${String(MIN_PASSWORD_LENGTH)} caracteres.`}
      >
        {(control) => (
          <Input
            {...control}
            type="password"
            autoComplete="new-password"
            disabled={busy}
            value={draft.password}
            onChange={(event) => patch({ password: event.currentTarget.value })}
          />
        )}
      </Field>
      <div className="sm:col-span-2">
        <Button type="submit" disabled={busy || !complete} loading={busy}>
          Crear cuenta
        </Button>
      </div>
    </form>
  );
};

export const StaffPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const managed = useManagedStaffList(
    session.client,
    authenticated?.csrfToken ?? '',
    session.handleFailure,
  );

  if (authenticated === null) return <></>;

  const busy = managed.mutation.status === 'loading';
  const selfId = authenticated.user.id;

  return (
    <section aria-labelledby="staff-heading" className="space-y-6">
      <div>
        <h2 className="m-0" id="staff-heading">
          Personal
        </h2>
        <p className="mb-0 text-muted-foreground">
          Crea y administra las cuentas que inician sesión en el panel.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle level={3}>Cuentas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {managed.state.status === 'loading' ? (
            <p role="status">Cargando cuentas…</p>
          ) : managed.state.status === 'error' ? (
            <StaffFailure failure={managed.state.failure} />
          ) : managed.state.data.length === 0 ? (
            <EmptyState
              icon={icons.users}
              title="Sin cuentas"
              description="Crea la primera cuenta de personal más abajo."
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {managed.state.data.map((member) => (
                    <StaffRow
                      key={member.id}
                      member={member}
                      isSelf={member.id === selfId}
                      busy={busy}
                      managed={managed}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {managed.mutation.status === 'error' ? (
            <StaffFailure failure={managed.mutation.failure} />
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle level={3}>Nueva cuenta</CardTitle>
        </CardHeader>
        <CardContent>
          <CreateStaffForm
            busy={busy}
            onCreate={async (draft) => {
              const result = await managed.create({
                nombre: draft.nombre.trim(),
                email: draft.email.trim(),
                rol: draft.rol,
                password: draft.password,
              });
              if (result.ok) toast({ title: 'Cuenta creada' });
              return result.ok;
            }}
          />
        </CardContent>
      </Card>
    </section>
  );
};
